import React, { useState, useEffect, useCallback } from 'react';
// Link больше не нужен для фильтров, FaArrowLeft и FaSave для интерфейса фильтров
import { FaPlus, FaTrash, FaPaperPlane, FaFilter, FaArrowLeft, FaSave } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext'; 
import './MailingManagement.css'; 
// Импортируем стили, которые могли быть специфичны для MailingDetailPage
// Если MailingDetailPage.css содержит только специфичные для страницы стили,
// то их нужно будет либо перенести в MailingManagement.css, либо создать отдельный CSS для фильтров.
// Пока предположим, что основные стили для формы фильтров можно будет адаптировать или они уже есть.
// import './MailingDetailPage.css'; // Возможно, понадобится, если стили сильно отличаются

const MailingManagement = () => {
  const [mailings, setMailings] = useState([]);
  const [loading, setLoading] = useState(true); // Загрузка списка рассылок
  const [error, setError] = useState(null);
  const { user } = useAuth(); 
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newMailingName, setNewMailingName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Состояния для отображения фильтров и выбранной рассылки
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMailingForFilters, setSelectedMailingForFilters] = useState(null);
  const [loadingFilters, setLoadingFilters] = useState(false); // Для загрузки деталей рассылки и контента
  const [filtersError, setFiltersError] = useState(null);

  // Состояния для самих фильтров (из MailingDetailPage.js)
  const [sendToAll, setSendToAll] = useState(false); // Новый фильтр "Всем"
  const [registrationFilterType, setRegistrationFilterType] = useState('all'); // 'all' или 'period'
  const [joinedDateFrom, setJoinedDateFrom] = useState('');
  const [joinedDateTo, setJoinedDateTo] = useState('');
  const [purchaseFilterType, setPurchaseFilterType] = useState('any'); // 'any', 'none', 'specific'
  const [purchasedItems, setPurchasedItems] = useState([]);
  const [purchasedItemsLogic, setPurchasedItemsLogic] = useState('OR');
  const [availableContent, setAvailableContent] = useState([]);
  const [showPurchasedItemsFilter, setShowPurchasedItemsFilter] = useState(false); // Остается для UI раскрытия списка
  const [estimatedRecipients, setEstimatedRecipients] = useState(0); // Для оценки получателей


  const fetchMailings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      const response = await fetch('/api/mailings', {
        headers: {
          'X-Telegram-ID': telegramId,
        },
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || `Ошибка HTTP: ${response.status}`);
      }
      const data = await response.json();
      setMailings(data.data || []);
    } catch (err) {
      console.error('Ошибка при загрузке рассылок:', err);
      setError(err.message);
      setMailings([]); // Очищаем список при ошибке
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!showFilters) { // Загружаем рассылки, только если не показываем фильтры
      fetchMailings();
    }
  }, [fetchMailings, showFilters]);
  
  // Функция для загрузки доступного контента для фильтра
  const fetchAvailableContent = useCallback(async () => {
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      const response = await fetch('/api/content?published=true&isFree=false', {
        headers: { 'X-Telegram-ID': telegramId },
      });
      if (!response.ok) {
        throw new Error('Не удалось загрузить список контента для фильтров');
      }
      const data = await response.json();
      setAvailableContent(Array.isArray(data) ? data : (data.content || []));
    } catch (err) {
      console.error("Ошибка при загрузке доступного контента:", err);
      setFiltersError(err.message); // Используем filtersError для ошибок в режиме фильтров
    }
  }, [user]);

  // Функция для загрузки деталей конкретной рассылки (для фильтров)
  const fetchSelectedMailingDetails = useCallback(async (mailingId) => {
    setLoadingFilters(true);
    setFiltersError(null);
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      const response = await fetch(`/api/mailings/${mailingId}`, {
        headers: { 'X-Telegram-ID': telegramId },
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || `Ошибка HTTP: ${response.status}`);
      }
      const data = await response.json();
      setSelectedMailingForFilters(data.data); // Обновляем всю информацию о выбранной рассылке
      // Инициализируем состояния фильтров из data.data.filters
      const filters = data.data.filters || {};
      setSendToAll(filters.sendToAll ?? false);
      
      if (filters.joinedDateRange && (filters.joinedDateRange.from || filters.joinedDateRange.to)) {
        setRegistrationFilterType('period');
        setJoinedDateFrom(filters.joinedDateRange.from ? new Date(filters.joinedDateRange.from).toISOString().split('T')[0] : '');
        setJoinedDateTo(filters.joinedDateRange.to ? new Date(filters.joinedDateRange.to).toISOString().split('T')[0] : '');
      } else {
        setRegistrationFilterType('all');
        setJoinedDateFrom('');
        setJoinedDateTo('');
      }

      if (filters.hasNotPurchasedAnything === true) {
        setPurchaseFilterType('none');
        setShowPurchasedItemsFilter(false); 
      } else if (filters.purchasedItems && filters.purchasedItems.length > 0) {
        setPurchaseFilterType('specific');
        const isMailingFinalized = data.data.status === 'sent' || data.data.status === 'partially_sent' || data.data.status === 'sending';
        // Если рассылка завершена/отправляется и есть конкретные купленные товары, показываем список
        setShowPurchasedItemsFilter(isMailingFinalized); 
      } else {
        setPurchaseFilterType('any'); 
        setShowPurchasedItemsFilter(false);
      }
      
      setPurchasedItems(filters.purchasedItems?.map(item => item._id || item) || []);
      setPurchasedItemsLogic(filters.purchasedItemsLogic || 'OR');
      
      // Установка количества получателей
      const stats = data.data.statistics;
      const status = data.data.status;
      if (status === 'sent' || status === 'partially_sent') {
        // Для завершенных рассылок показываем фактическое количество тех, кому было доставлено
        setEstimatedRecipients(stats?.uniqueUsersReached || 0);
      } else {
        // Для других статусов (черновик, готова к отправке, отправляется) показываем оценку
        setEstimatedRecipients(stats?.estimatedRecipients || 0);
      }

    } catch (err) {
      console.error('Ошибка при загрузке деталей рассылки для фильтров:', err);
      setFiltersError(err.message);
    } finally {
      setLoadingFilters(false);
    }
  }, [user]);


  const handleShowFiltersView = (mailing) => {
    setSelectedMailingForFilters(mailing); // Сначала устанавливаем, чтобы иметь ID
    setShowFilters(true);
    fetchSelectedMailingDetails(mailing._id);
    fetchAvailableContent();
  };

  const handleCloseFiltersView = () => {
    setShowFilters(false);
    setSelectedMailingForFilters(null);
    // Сброс состояний фильтров
    setSendToAll(false);
    setRegistrationFilterType('all');
    setJoinedDateFrom('');
    setJoinedDateTo('');
    setPurchaseFilterType('any');
    setPurchasedItems([]);
    setPurchasedItemsLogic('OR');
    setAvailableContent([]);
    setShowPurchasedItemsFilter(false); // Это состояние для UI, его тоже сбрасываем
    setEstimatedRecipients(0);
    setFiltersError(null);
    // Перезагружаем список рассылок, так как могли быть изменения
    fetchMailings(); 
  };

  const fetchEstimatedRecipients = useCallback(async (filters) => {
    if (!selectedMailingForFilters?._id) return;
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      const response = await fetch(`/api/mailings/${selectedMailingForFilters._id}/estimate-recipients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-ID': telegramId,
        },
        body: JSON.stringify(filters),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Не удалось оценить количество получателей');
      }
      const data = await response.json();
      setEstimatedRecipients(data.estimatedRecipients);
    } catch (err) {
      console.error('Ошибка при оценке получателей:', err);
      // Можно установить какое-то сообщение об ошибке для оценки, если нужно
    }
  }, [selectedMailingForFilters, user]);
  
  // Эффект для вызова оценки при изменении фильтров
  useEffect(() => {
    if (showFilters && selectedMailingForFilters) {
      const isMailingFinalized = 
        selectedMailingForFilters.status === 'sent' || 
        selectedMailingForFilters.status === 'partially_sent' ||
        selectedMailingForFilters.status === 'sending';

      if (!isMailingFinalized) { // Вызываем оценку, только если рассылка не в финальном/активном статусе
        const currentFilters = {
          sendToAll,
          joinedDateRange: registrationFilterType === 'period' ? { from: joinedDateFrom || null, to: joinedDateTo || null } : null,
          hasNotPurchasedAnything: purchaseFilterType === 'none',
          purchasedItems: purchaseFilterType === 'specific' ? purchasedItems : [],
          purchasedItemsLogic: purchaseFilterType === 'specific' ? purchasedItemsLogic : 'OR',
        };
        fetchEstimatedRecipients(currentFilters);
      }
      // Если рассылка финализирована, estimatedRecipients уже должен быть установлен из fetchSelectedMailingDetails
      // и не должен меняться при изменении (заблокированных) фильтров в UI.
      // Однако, если пользователь меняет заблокированные фильтры (что не должно быть возможно, но для безопасности),
      // и мы хотим, чтобы "Получателей" всегда отражало фактическое число для завершенных, можно добавить:
      else if (selectedMailingForFilters.statistics) {
         setEstimatedRecipients(selectedMailingForFilters.statistics.uniqueUsersReached || 0);
      }
    }
  }, [sendToAll, registrationFilterType, joinedDateFrom, joinedDateTo, purchaseFilterType, purchasedItems, purchasedItemsLogic, showFilters, selectedMailingForFilters, fetchEstimatedRecipients]);


  const handleSaveFilters = async () => {
    if (!selectedMailingForFilters) return;

    const filtersToSave = {
        sendToAll,
        joinedDateRange: registrationFilterType === 'period' ? { from: joinedDateFrom || null, to: joinedDateTo || null } : null,
        hasNotPurchasedAnything: purchaseFilterType === 'none',
        // Если sendToAll=true, то purchasedItems и purchasedItemsLogic не имеют значения, но для консистентности можно отправлять их текущие значения или пустые/дефолтные
        purchasedItems: sendToAll || purchaseFilterType === 'none' ? [] : (purchaseFilterType === 'specific' ? purchasedItems : []),
        purchasedItemsLogic: sendToAll || purchaseFilterType === 'none' ? 'OR' : (purchaseFilterType === 'specific' ? purchasedItemsLogic : 'OR')
    };
    console.log('Сохраняемые фильтры:', filtersToSave);
    try {
        const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
        const response = await fetch(`/api/mailings/${selectedMailingForFilters._id}/filters`, {
            method: 'PUT',
            headers: { 
                'Content-Type': 'application/json',
                'X-Telegram-ID': telegramId 
            },
            body: JSON.stringify(filtersToSave)
        });
        if (!response.ok) {
            const errData = await response.json();
            throw new Error(errData.message || 'Не удалось сохранить фильтры');
        }
        const saveData = await response.json(); // Бэкенд теперь возвращает обновленную рассылку
        alert('Фильтры успешно сохранены!');

        if (saveData.success && saveData.data) {
            const updatedMailingFromServer = saveData.data;

            // Обновляем selectedMailingForFilters для UI текущего вида фильтров
            setSelectedMailingForFilters(updatedMailingFromServer);
            // Обновляем оценку получателей в состоянии для UI фильтров
            setEstimatedRecipients(updatedMailingFromServer.statistics?.estimatedRecipients || 0);
            
            // Обновляем эту рассылку в общем списке mailings
            setMailings(prevMailings =>
                prevMailings.map(m =>
                    m._id === updatedMailingFromServer._id
                        ? updatedMailingFromServer // Заменяем на полностью обновленные данные с сервера
                        : m
                )
            );
        } else {
            // Если бэкенд не вернул data, или success false, но статус 200 (маловероятно)
            // Можно попробовать перезапросить детали, как запасной вариант
            fetchSelectedMailingDetails(selectedMailingForFilters._id);
        }

    } catch (err) {
        console.error("Ошибка при сохранении фильтров:", err);
        alert(`Ошибка: ${err.message}`);
        setFiltersError(err.message);
    }
  };

  // Функция handleSendMailing больше не используется, так как запуск происходит из списка рассылок через handleStartMailingViaBot
  // const handleSendMailing = async () => { ... };

  const handleShowCreateForm = () => {
    setShowCreateForm(true);
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewMailingName('');
  };

  const handleConfirmCreateMailing = async () => {
    if (!newMailingName.trim()) {
      alert('Название рассылки не может быть пустым.');
      return;
    }
    setIsCreating(true);
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      const response = await fetch('/api/mailings', { // Новый эндпоинт
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-ID': telegramId,
        },
        body: JSON.stringify({ name: newMailingName.trim() }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Не удалось создать рассылку');
      }
      await response.json(); 
      alert('Рассылка успешно создана. Теперь вы можете добавить для нее сообщения.');
      setShowCreateForm(false);
      setNewMailingName('');
      fetchMailings(); 
    } catch (err) {
      console.error('Ошибка при создании рассылки:', err);
      alert(`Ошибка: ${err.message}`);
    } finally {
      setIsCreating(false);
    }
  };
  
  const handleAddMessages = (mailingId) => { // Убираем hasMessagesCurrently
    const botUsername = process.env.REACT_APP_TELEGRAM_BOT_USERNAME;
    if (!botUsername) {
      alert('Ошибка: Имя пользователя бота не настроено.');
      console.error('REACT_APP_TELEGRAM_BOT_USERNAME is not set in .env');
      return;
    }
    // Просто открываем ссылку на бота, передавая ID рассылки.
    // Всю логику по проверке существующих сообщений и предложению их заменить будет выполнять бот.
    const telegramLink = `https://t.me/${botUsername}?start=add_mailing_messages_${mailingId}`;
    if (window.Telegram && window.Telegram.WebApp) {
      window.Telegram.WebApp.openTelegramLink(telegramLink);
      window.Telegram.WebApp.close();
    } else {
      alert('Для работы с сообщениями, пожалуйста, перейдите в Telegram-бот.');
      window.open(telegramLink, '_blank');
    }
  };

  const handleStartMailingViaBot = (mailingId) => {
    const botUsername = process.env.REACT_APP_TELEGRAM_BOT_USERNAME;
    if (!botUsername) {
      alert('Ошибка: Имя пользователя бота не настроено.');
      console.error('REACT_APP_TELEGRAM_BOT_USERNAME is not set in .env');
      return;
    }
    const telegramLink = `https://t.me/${botUsername}?start=startwith_${mailingId}`;
    if (window.Telegram && window.Telegram.WebApp) {
      window.Telegram.WebApp.openTelegramLink(telegramLink);
      window.Telegram.WebApp.close(); // Закрываем веб-приложение после открытия ссылки
    } else {
      alert('Для запуска рассылки, пожалуйста, перейдите в Telegram-бот.');
      window.open(telegramLink, '_blank');
    }
  };

  const handleDeleteMailing = async (mailingId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту рассылку? Это действие необратимо.')) {
      return;
    }
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      const response = await fetch(`/api/mailings/${mailingId}`, {
        method: 'DELETE',
        headers: {
          'X-Telegram-ID': telegramId,
        },
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Не удалось удалить рассылку');
      }
      alert('Рассылка успешно удалена.');
      fetchMailings(); // Обновляем список
    } catch (err) {
      console.error('Ошибка при удалении рассылки:', err);
      alert(`Ошибка: ${err.message}`);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    }).format(date);
  };

  // Функция для получения иконки, класса и русского названия статуса
  const getStatusDisplayInfo = (status) => {
    switch (status) {
      case 'draft':
        return { icon: <FaPaperPlane />, className: 'status-draft', title: 'Черновик', russianName: 'Черновик' }; 
      case 'awaiting_messages':
        return { icon: <FaPaperPlane />, className: 'status-awaiting_messages', title: 'Ожидает сообщений', russianName: 'Ожидает сообщений' };
      case 'ready_to_send':
        return { icon: <FaPaperPlane />, className: 'status-ready_to_send', title: 'Готова к отправке', russianName: 'Готова к отправке' };
      case 'sending':
        return { icon: <FaPaperPlane />, className: 'status-sending', title: 'Отправляется', russianName: 'Отправляется' };
      case 'sent':
        return { icon: <FaPaperPlane />, className: 'status-sent', title: 'Отправлена', russianName: 'Отправлена' };
      case 'partially_sent':
        return { icon: <FaPaperPlane />, className: 'status-partially_sent', title: 'Частично отправлена', russianName: 'Частично отправлена' };
      case 'failed':
        return { icon: <FaPaperPlane />, className: 'status-failed', title: 'Ошибка отправки', russianName: 'Ошибка отправки' };
      case 'pending_content':
        return { icon: <FaPaperPlane />, className: 'status-pending_content', title: 'Ожидает контент', russianName: 'Ожидает контент' };
      default:
        return { icon: <FaPaperPlane />, className: '', title: status, russianName: status }; // Для неизвестных статусов оставляем как есть
    }
  };

  // Условный рендеринг: список рассылок или интерфейс фильтров
  if (showFilters && selectedMailingForFilters) {
    // Рендеринг интерфейса фильтров (взято и адаптировано из MailingDetailPage.js)
    if (loadingFilters) return <div className="loading-state"><div className="loading-spinner"></div><p>Загрузка деталей рассылки и фильтров...</p></div>;
    if (filtersError) return <div className="error-message">Ошибка: {filtersError} <button onClick={handleCloseFiltersView} className="admin-button">Назад</button></div>;
    if (!selectedMailingForFilters) return <div className="empty-state">Рассылка не найдена. <button onClick={handleCloseFiltersView} className="admin-button">Назад</button></div>;
    
    const statusInfo = getStatusDisplayInfo(selectedMailingForFilters.status);
    const isEditingDisabled = selectedMailingForFilters.status === 'sending' || selectedMailingForFilters.status === 'sent' || selectedMailingForFilters.status === 'partially_sent';

    // Используем uniqueUsersReached и uniqueUsersFailed для статистики по пользователям
    const usersReachedCount = selectedMailingForFilters.statistics?.uniqueUsersReached || 0;
    const usersFailedCount = selectedMailingForFilters.statistics?.uniqueUsersFailed || 0;
    // hasBeenSent можно определять по статусу или по тому, что хотя бы один из счетчиков пользователей больше нуля
    const hasBeenSent = selectedMailingForFilters.status === 'sent' || 
                        selectedMailingForFilters.status === 'partially_sent' || 
                        selectedMailingForFilters.status === 'sending' || 
                        (usersReachedCount > 0 || usersFailedCount > 0);


    return (
      <div className="mailing-detail-view mailing-management"> 
        <div className="page-header">
          <button onClick={handleCloseFiltersView} className="admin-button icon-button">
            <FaArrowLeft /> Назад к списку
          </button>
          {/* <h2>Фильтры для: {selectedMailingForFilters.name}</h2> */}
        </div>

        <div className="mailing-info">
          <p><strong>Название:</strong> {selectedMailingForFilters.name}</p>
          <p><strong>Статус:</strong> {statusInfo.russianName}</p>
          <p><strong>Сообщений:</strong> {selectedMailingForFilters.messages?.length || 0}</p>
          <p><strong>Создана:</strong> {formatDate(selectedMailingForFilters.createdAt)}</p>
          <p><strong>Получателей:</strong> {estimatedRecipients}</p>
          <p><strong>Доставлено / Ошибка:</strong> {hasBeenSent ? `${usersReachedCount} / ${usersFailedCount}` : 'не отправлялось'}</p>
        </div>

        <h3><FaFilter /> Фильтры получателей</h3>
        <div className={`filters-form ${sendToAll ? 'filters-disabled' : ''}`}>
          <div className="form-group">
            <label htmlFor="sendToAllToggle" className="toggle-switch-label">
              Отправить всем пользователям:
              <button 
                type="button"
                className={`admin-button toggle-button ${sendToAll ? 'active' : ''}`} 
                onClick={() => !isEditingDisabled && setSendToAll(!sendToAll)}
                disabled={isEditingDisabled}
              >
                {sendToAll ? 'Да' : 'Нет'}
              </button>
            </label>
          </div>

          <fieldset disabled={sendToAll || isEditingDisabled} className="filter-fieldset">
            <legend className="sr-only">Дополнительные фильтры</legend>
            <div className="form-group">
              <label>Дата регистрации (входа в бот):</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input 
                    type="radio" 
                    name="registrationFilterType" 
                    value="all" 
                    checked={registrationFilterType === 'all'} 
                    onChange={(e) => setRegistrationFilterType(e.target.value)}
                    disabled={sendToAll || isEditingDisabled}
                  />
                  Все даты
                </label>
                <label className="radio-label">
                  <input 
                    type="radio" 
                    name="registrationFilterType" 
                    value="period" 
                    checked={registrationFilterType === 'period'} 
                    onChange={(e) => setRegistrationFilterType(e.target.value)}
                    disabled={sendToAll || isEditingDisabled}
                  />
                  Выбрать период
                </label>
              </div>
              {registrationFilterType === 'period' && (
                <div className="date-range-group">
                  <input 
                    type="date" 
                    value={joinedDateFrom} 
                    onChange={(e) => setJoinedDateFrom(e.target.value)}
                    placeholder="С"
                    disabled={sendToAll || isEditingDisabled}
                  />
                  <span>-</span>
                  <input 
                    type="date" 
                    value={joinedDateTo} 
                    onChange={(e) => setJoinedDateTo(e.target.value)}
                    placeholder="По"
                    disabled={sendToAll || isEditingDisabled}
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Фильтр по купленным материалам:</label>
              <div className="radio-group">
                 <label className="radio-label">
                  <input 
                    type="radio" 
                    name="purchaseFilterType" 
                    value="any" 
                    checked={purchaseFilterType === 'any'} 
                    onChange={(e) => setPurchaseFilterType(e.target.value)}
                    disabled={sendToAll || isEditingDisabled}
                  />
                  Не важно (любые покупки)
                </label>
                <label className="radio-label">
                  <input 
                    type="radio" 
                    name="purchaseFilterType" 
                    value="none" 
                    checked={purchaseFilterType === 'none'} 
                    onChange={(e) => setPurchaseFilterType(e.target.value)}
                    disabled={sendToAll || isEditingDisabled}
                  />
                  Не купил ничего
                </label>
                <label className="radio-label">
                  <input 
                    type="radio" 
                    name="purchaseFilterType" 
                    value="specific" 
                    checked={purchaseFilterType === 'specific'} 
                    onChange={(e) => {
                        setPurchaseFilterType(e.target.value);
                        if (e.target.value === 'specific' && !showPurchasedItemsFilter) {
                            setShowPurchasedItemsFilter(true);
                        }
                    }}
                    disabled={sendToAll || isEditingDisabled}
                  />
                  Купил конкретные материалы
                </label>
              </div>

              {purchaseFilterType === 'specific' && (
                <>
                  <button 
                    type="button" 
                    onClick={() => setShowPurchasedItemsFilter(!showPurchasedItemsFilter)}
                    className="accordion-toggle-button"
                    disabled={sendToAll || isEditingDisabled}
                  >
                    {showPurchasedItemsFilter ? 'Скрыть' : 'Показать'} список материалов 
                    ({purchasedItems.length > 0 ? `${purchasedItems.length} выбрано` : 'не выбраны'})
                  </button>
                  {showPurchasedItemsFilter && (
                    <div className="purchased-items-filter">
                      <div className="form-group logic-selector">
                        <label>Логика для купленных постов:</label>
                        <select 
                            value={purchasedItemsLogic} 
                            onChange={(e) => setPurchasedItemsLogic(e.target.value)}
                            disabled={sendToAll || isEditingDisabled}
                        >
                          <option value="OR">Купил хотя бы ОДИН из выбранных</option>
                          <option value="AND">Купил ВСЕ выбранные</option>
                        </select>
                      </div>
                      <div className="items-checkbox-list">
                        {availableContent.length > 0 ? (
                          availableContent.map(contentItem => (
                            <div key={contentItem._id} className="checkbox-item">
                              <input 
                                type="checkbox"
                                id={`content-filter-${contentItem._id}`} 
                                value={contentItem._id}
                                checked={purchasedItems.includes(contentItem._id)}
                                onChange={(e) => {
                                  const itemId = e.target.value;
                                  setPurchasedItems(prev => 
                                    e.target.checked ? [...prev, itemId] : prev.filter(id => id !== itemId)
                                  );
                                }}
                                disabled={sendToAll || isEditingDisabled}
                              />
                              <label htmlFor={`content-filter-${contentItem._id}`}>{contentItem.title} ({contentItem.price} ₽)</label>
                            </div>
                          ))
                        ) : (
                          <p>Нет доступных постов для выбора.</p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </fieldset>
          
          {!isEditingDisabled && (
            <button onClick={handleSaveFilters} className="admin-button primary wide-button">
              <FaSave /> Сохранить фильтры
            </button>
          )}
          {isEditingDisabled && (
            <p className="info-text">Редактирование фильтров недоступно, так как рассылка находится в процессе отправки или уже отправлена.</p>
          )}
        </div>
        {/* Кнопка "Запустить рассылку" удалена из этого блока, actions-footer тоже удален, так как кнопка запуска теперь в списке */}
      </div>
    );
  }

  return (
    <div className="mailing-management"> 
      <div className="page-header"> 
        {/* <h2>Управление рассылками</h2> */}
        {!showCreateForm && !showFilters && (
          <button className="add-button" onClick={handleShowCreateForm}>
            <FaPlus /> Создать рассылку
          </button>
        )}
      </div>

      {showCreateForm && (
        <div className="create-mailing-form">
          <h3>Новая рассылка</h3>
          <input
            type="text"
            placeholder="Введите название рассылки"
            value={newMailingName}
            onChange={(e) => setNewMailingName(e.target.value)}
            disabled={isCreating}
          />
          <div className="form-buttons">
            <button onClick={handleConfirmCreateMailing} disabled={isCreating || !newMailingName.trim()} className="admin-button confirm-button">
              {isCreating ? 'Создание...' : 'Подтвердить и создать'}
            </button>
            <button onClick={handleCancelCreate} disabled={isCreating} className="admin-button cancel-button">
              Отмена
            </button>
          </div>
        </div>
      )}

      {!showCreateForm && !showFilters && loading && mailings.length === 0 && (
         <div className="loading-state">
           <div className="loading-spinner"></div>
           <p>Загрузка рассылок...</p>
         </div>
      )}
      {!showCreateForm && !showFilters && !loading && error && (
        <div className="error-message">Ошибка при загрузке рассылок: {error}</div>
      )}
      {!showCreateForm && !showFilters && !loading && !error && mailings.length === 0 && (
        <div className="empty-state">
          <FaPaperPlane size={50} />
          <p>Рассылок пока нет.</p>
          <p>Нажмите "Создать рассылку", чтобы начать.</p>
        </div>
      )}
      
      {!showCreateForm && !showFilters && mailings.length > 0 && (
        <div className="admin-table-wrapper content-list"> 
          {mailings.map((mailing) => {
            const { icon, className: statusClassName, title: statusTitle } = getStatusDisplayInfo(mailing.status);
            return (
              <div key={mailing._id} className="content-item"> 
                <div className="content-item-header"> 
                  <div className="content-title"> 
                    <h3>{mailing.name}</h3>
                    <div className="content-meta">
                      <div>
                        Сообщений: {mailing.messages?.length || 0} • Получателей: {mailing.statistics?.estimatedRecipients || 0}
                      </div>
                      <div>
                        Доставлено: {mailing.statistics?.uniqueUsersReached || 0} • Ошибка: {mailing.statistics?.uniqueUsersFailed || 0}
                      </div>
                      <div>Создана: {formatDate(mailing.createdAt)}</div>
                      {(mailing.status === 'sent' || mailing.status === 'partially_sent') && mailing.statistics?.finishedAt && (
                        <div>Отправлена: {formatDate(mailing.statistics.finishedAt)}</div>
                      )}
                       {(mailing.status === 'sending') && mailing.statistics?.startedAt && !mailing.statistics?.finishedAt && (
                        <div>В процессе с: {formatDate(mailing.statistics.startedAt)}</div>
                      )}
                    </div>
                  </div>
                  <div className="content-status"> 
                    <span className={`status-icon ${statusClassName}`} title={statusTitle}>
                      {icon} 
                    </span>
                  </div>
                </div>
                <div className="content-actions"> 
                  <div className="button-row"> 
                    <button
                      className="admin-button" 
                      onClick={() => handleAddMessages(mailing._id)} // Вызываем без второго аргумента
                      title="Сообщения" // Упрощаем title, так как логика теперь в боте
                      disabled={mailing.status === 'sending'} // Оставляем disabled только для статуса 'sending'
                    >
                      <FaPaperPlane /> Сообщения
                    </button>
                    <button 
                      onClick={() => handleShowFiltersView(mailing)} 
                      className="admin-button" 
                      title="Настроить фильтры и детали"
                    >
                      <FaFilter /> Фильтры
                    </button>
                  </div>
                  <div className="button-row">
                    { /* Логика кнопки Запустить/Не готова/В работе/Завершена */ }
                    {mailing.status === 'ready_to_send' && (mailing.filters?.configured || mailing.filters?.sendToAll) && mailing.messages?.length > 0 ? (
                        <button
                            className="admin-button success" // Зеленая кнопка
                            onClick={() => handleStartMailingViaBot(mailing._id)}
                            title="Запустить рассылку через бота"
                        >
                            Запустить
                        </button>
                    ) : mailing.status === 'sending' ? (
                        <button className="admin-button" disabled>В работе...</button>
                    ) : mailing.status === 'sent' || mailing.status === 'partially_sent' ? (
                        <button className="admin-button" disabled>Завершена</button>
                    ) : (
                        <button 
                            className="admin-button not-ready-button" // Класс для стилизации "Не готова"
                            disabled // Делаем кнопку некликабельной
                            title="Рассылка не готова: настройте сообщения и фильтры" // Обновляем title
                        >
                            Не готова
                        </button>
                    )}
                    <button
                      className="admin-button admin-button-delete" 
                      onClick={() => handleDeleteMailing(mailing._id)}
                      title="Удалить рассылку"
                      disabled={mailing.status === 'sending'}
                    >
                      <FaTrash /> Удалить
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MailingManagement;
